import {
  FlexProps,
  Icon,
  Progress,
  Table,
  TableBodyProps,
  TableCellProps,
  TableColumnHeaderProps,
  TableHeaderProps,
  TableRootProps,
  TableRowProps,
  TableScrollAreaProps,
  useCallbackRef,
} from '@chakra-ui/react';
import {
  DEFAULT_ROWS,
  FilterType,
  TDataTablePager,
} from '@monetize/types/common';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Fragment,
  ReactNode,
  SyntheticEvent,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { useNonInitialEffect } from '../../../hooks/useNonInitialEffect';
import { MBox, MFlex, MSkeleton } from '../chakra';
import { LoadMore } from '../LoadMore';
import MEmptyDataPlaceholder, {
  MEmptyPlaceholderProps,
} from '../MEmptyDataPlaceholder';
import {
  AppCell,
  AppColumnDef,
  AppFeatures,
  AppHeader,
  AppReactTable,
  AppRow,
  AppTableOptions,
  ColumnSizingState,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
  appTableFeatures,
  flexRender,
  functionalUpdate,
  useTable,
} from './appTable';
import {
  DataTableActiveFilters,
  ExtraActiveFilter,
} from './DataTableActiveFilters';
import { DateTableHeaderFilter } from './dataTableFilters/DateTableHeaderFilter';
import { ACTION_COLUMN_KEY } from './dataTableNewRenderers';
import { DataTablePagination } from './DataTablePagination';
import { DataTableFilterRef, DataTableRef } from './DataTableTypes';
import {
  TableData,
  findLastNonPinnedIndex,
  getColumnFilterKey,
  prepareTableData,
  pruneRowSelection,
  transformFiltersForServer,
  transformInitialFiltersToMap,
} from './tableUtilsNew';

export type MEmptyFilterResultsProps = {
  mainMessage?: string;
  smallMessage?: string | null;
  btnLabel?: string | null;
  secondaryBtnLabel?: string | null;
} & ({ to: string; onReload?: never } | { to?: never; onReload?: () => void });

// Default function to get row id
const getRowIdFn = (row: any) => row.id;

const borderColor = 'gray.200';

/**
 * Module-scope style objects and prop defaults.
 *
 * These must not be re-created per render: they are compared by reference by
 * the `memo`-ised row components below, so a fresh object literal would defeat
 * the memoisation for every table that relies on the default.
 */
const ROW_HOVER_STYLES = {
  backgroundColor: '#fafaff',
  borderBottomColor: '#4659da',
};

// The Table theme drops the last row's bottom border (for tables inside a
// bordered card). Every row here carries its own divider and hover underline,
// so keep it on the last row too.
const ROW_LAST_STYLES = {
  borderBottom: '1px solid',
  borderBottomColor: 'border.subtle',
};

const ROW_EXPANDED_ZINDEX_STYLES = {
  '&:has([aria-expanded="true"])': {
    zIndex: 10,
  },
};

/**
 * Whether a row event started inside the row's own DOM. Modals, menus and
 * popovers opened from a cell portal out of the row, but React still bubbles
 * their events up the component tree to the row's handlers — so typing a
 * reason into a row action's cancel modal would otherwise navigate to the row.
 */
const isEventFromRowDom = (event: SyntheticEvent<Element>) =>
  event.currentTarget.contains(event.target as Node);

const CELL_HOVER_STYLES = {
  'tr[data-row]:hover > &': {
    backgroundColor: '#fafaff !important',
  },
};

const EMPTY_TABLE_PROPS: ExtraTableProps = {};
const EMPTY_CONTAINER_PROPS: FlexProps = {};
const EMPTY_EXTRA_PROPS: DataTableProps<never>['extraProps'] = {};
const EMPTY_FILTER_DATA_DISPLAY_MAP: Parameters<typeof prepareTableData>[1] =
  {};
const EMPTY_TEXT_SEARCH_KEYS: Parameters<typeof prepareTableData>[2] = [];
const EMPTY_SORTING_STATE: SortingState = [];
const EMPTY_PINNED_COLUMNS: string[] = [];

/**
 * The table state `DataTable` re-renders for: everything except
 * `columnResizing`, which `ColumnResizeHandle` subscribes to on its own.
 */
type DataTableSelectedState = Omit<TableState<AppFeatures>, 'columnResizing'>;

// Utility functions to calculate border styles for pinned columns
const getHeaderBorderStyles = ({
  isPinned,
  isFirstColumn,
  isGroup,
  skipBorder = false,
}: {
  isPinned: false | 'start' | 'end';
  isFirstColumn: boolean;
  isGroup: boolean;
  skipBorder?: boolean;
}) => {
  if (skipBorder) {
    return {};
  }
  // For column groups, show entire border
  if (isGroup) {
    return {
      background: 'gray.50',
      border: '1px solid',
      borderColor,
    };
  }
  // No `borderBottom` here: it would cancel the theme's header bottom border,
  // the only line under the header. v2's theme out-ranked a `none` set here.
  return {
    borderTop: 'none',
    borderColor,
    borderLeft:
      isPinned === 'end' || !isFirstColumn
        ? `1px solid var(--chakra-colors-gray-300)`
        : 'none',
    borderRight: 'none',
    borderRightColor: isPinned === 'start' ? borderColor : undefined,
  };
};

/**
 * Calculate border styles for body cells
 */
const getCellBorderStyles = (
  isPinned: false | 'start' | 'end',
): TableCellProps => {
  if (!isPinned) {
    return {};
  }
  if (isPinned === 'start') {
    return {
      background: '#ffffff',
      borderRight: '1px solid rgba(0,0,0,0.08)',
      transition: 'box-shadow 160ms ease',
    };
  }
  return {
    background: '#ffffff',
    borderLeft: '1px solid rgba(0,0,0,0.08)',
    transition: 'box-shadow 160ms ease',
  };
};

interface ExtraTableProps {
  table?: TableRootProps;
  head?: TableHeaderProps;
  headRow?: TableRowProps;
  headCell?: TableColumnHeaderProps;
  body?: TableBodyProps;
  bodyRow?: TableRowProps;
  cell?: TableCellProps;
}

interface DataTableProps<T extends object> {
  /**
   * Enable row virtualization for large datasets.
   * When false (default), uses standard document flow which supports expandable rows.
   * @default false
   */
  enableVirtualization?: boolean;
  emptyProps?: MEmptyPlaceholderProps;
  /**
   * Set to false to disable sorting for entire table.
   */
  enableSorting?: boolean;
  isLoading?: boolean;
  /**
   * Lock selection if true
   * Can be used while processing data etc..
   */
  selectionDisabled?: boolean;
  /** Defaults to row.id */
  columns: readonly AppColumnDef<any, any>[];
  data: T[];
  /** Enum conversion maps for filters */
  filterDataDisplayMap?: Parameters<typeof prepareTableData>[1];
  /** Keys to allow global text search against */
  textSearchKeys?: Parameters<typeof prepareTableData>[2];
  pinnedLeftColumns?: string[];
  pinnedRightColumns?: string[];
  /**
   * Extra props to pass to table
   */
  tableProps?: ExtraTableProps;
  containerProps?: FlexProps;
  state?: Partial<TableState<AppFeatures>>;
  initialSortingState?: SortingState;
  /**
   * Set to true for tables that do not have server side pagination/sorting/filtering
   * @default false
   */
  clientControlled?: boolean;
  extraProps?: {
    containerProps?: TableScrollAreaProps;
    tableProps?: TableRootProps;
    tableHeadProps?: TableHeaderProps;
  };
  /**
   * If true, skips generating filter values for columns
   * This should be true for large datasets or when no set filters are used
   * This improves performance of initializing the table
   */
  skipFilterValuePreprocessing?: boolean;
  getRowId?: (row: T) => string;
  /**
   * Fired with the number of selected rows.
   *
   * Only the count crosses the render boundary; read the rows themselves via
   * `DataTableRef.getSelectedRows()` inside an event handler. Passing the row
   * objects up re-renders the consuming page on every checkbox click, which is
   * prohibitively expensive on large datasets.
   */
  onSelectedRowCountChange?: (selectedRowCount: number) => void;
  /**
   * Fired with the number of rows currently passing filters/sort.
   * See `onSelectedRowCountChange` — read the rows via
   * `DataTableRef.getVisibleRows()`.
   */
  onVisibleRowCountChange?: (visibleRowCount: number) => void;
  /**
   * @param row Raw table row
   * @param tableRow React Query table row - allows accessing additional table API properties
   * @param openInNewWindow True when the user Ctrl/Cmd/Shift-clicked or used the middle mouse button
   */
  onRowClick?: (row: T, tableRow: AppRow<T>, openInNewWindow?: boolean) => void;
  /**
   * Enable pagination controls
   * @default false
   */
  enablePagination?: boolean;
  /**
   * Total number of elements across all pages (for server-side pagination)
   * If not provided, will use filtered row count (client-side)
   */
  totalElements?: number;
  /**
   * Total number of pages (for server-side pagination)
   * If not provided, will be calculated from totalElements and pageSize
   */
  totalPages?: number;
  /**
   * Pager state (similar to MDataTable)
   * If provided, table will use this for pagination/sorting state
   */
  pager?: TDataTablePager;
  /**
   * Pager setter (similar to MDataTable)
   */
  setPager?: (pager: TDataTablePager) => void;
  /**
   * Callback when filters change
   * Automatically resets pagination to page 0
   */
  onFiltersChange?: (filters: FilterType[]) => void;
  /**
   * Initial filters to apply when component mounts
   * Useful for restoring persisted filter state
   */
  initialFilters?: FilterType[];
  /**
   * Props for empty filter results state
   */
  emptyFilterResultsProps?: MEmptyFilterResultsProps;
  /**
   * If true, use the same empty placeholder for both filtered and unfiltered empty states
   * @default false
   */
  singleEmptyPlaceholder?: boolean;
  /**
   * Function to reset filters
   */
  resetFilter?: () => void;
  /**
   * Props to enable load-more pattern instead of standard pagination.
   * Mutually exclusive with enablePagination.
   */
  loadMoreProps?: {
    onLoadMore: () => void;
    hasMoreItem?: boolean;
  };
  /**
   * Child Rows
   *
   * @link https://tanstack.com/table/latest/docs/guide/expanding#enable-client-side-expanding
   */
  getSubRows?: AppTableOptions<T>['getSubRows'];
  /**
   * If expanding needs to be controlled externally
   * pass in state.expanded as well as this property to store expanded state outside the table
   *
   * @link https://tanstack.com/table/latest/docs/guide/expanding#expanded-rows-state
   */
  onExpandedChange?: AppTableOptions<T>['onExpandedChange'];
  /**
   * Custom Render function for child rows
   * getRowCanExpand can be `(row) => true` if all rows should allow expansion or can include custom logic
   *
   * @link https://tanstack.com/table/latest/docs/guide/expanding#custom-expanding-ui
   */
  customChildRow?: {
    getRowCanExpand: AppTableOptions<T>['getRowCanExpand'];
    renderCustomExpandedRow: (row: AppRow<T>) => ReactNode;
  };
  extraActiveFilters?: ExtraActiveFilter[];
}

/**
 * Check if a filter value represents a "reset" / no-filter state.
 * When true, the key should be removed from the columnFilters map.
 */
function isFilterValueEmpty(
  filterVariant: string | undefined,
  value: unknown,
): boolean {
  switch (filterVariant) {
    case 'SET':
      return !(value instanceof Set) || value.size === 0;
    case 'BOOLEAN':
      return value === 'ALL';
    case 'SELECTION':
      return value === 'ALL';
    case 'DATE': {
      if (value == null) {
        return true;
      }
      if (typeof value === 'object' && 'firstDate' in value) {
        const dateVal = value as {
          firstDate?: string | null;
          secondDate?: string | null;
        };
        return !dateVal.firstDate && !dateVal.secondDate;
      }
      return false;
    }
    case 'OWNER':
      return (
        value == null ||
        (typeof value === 'object' && !('ownerId' in (value as object)))
      );
    case 'NUMBER':
      return value == null;
    case 'TEXT':
      return value == null || (typeof value === 'string' && !value.trim());
    default:
      return value == null;
  }
}

// FIXME: figure out how to use generic props with forwardRef
const DataTableComponent = forwardRef<DataTableRef, DataTableProps<any>>(
  (
    {
      isLoading,
      selectionDisabled,
      columns,
      data: initialData,
      pinnedLeftColumns,
      pinnedRightColumns,
      filterDataDisplayMap = EMPTY_FILTER_DATA_DISPLAY_MAP,
      textSearchKeys = EMPTY_TEXT_SEARCH_KEYS,
      containerProps = EMPTY_CONTAINER_PROPS,
      tableProps = EMPTY_TABLE_PROPS,
      emptyProps,
      enableSorting = true,
      /**
       * Anything provided here overrides the internal state of the table
       */
      state,
      initialSortingState = EMPTY_SORTING_STATE,
      clientControlled = false,
      enablePagination = true,
      totalElements,
      totalPages,
      extraProps = EMPTY_EXTRA_PROPS,
      skipFilterValuePreprocessing = false,
      getRowId = getRowIdFn,
      onSelectedRowCountChange,
      onVisibleRowCountChange,
      onRowClick,
      pager,
      setPager,
      onFiltersChange,
      initialFilters,
      emptyFilterResultsProps,
      singleEmptyPlaceholder = false,
      resetFilter,
      getSubRows,
      onExpandedChange,
      customChildRow,
      loadMoreProps,
      enableVirtualization = false,
      extraActiveFilters,
    },
    ref,
  ) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [sorting, setSorting] = useState<SortingState>(initialSortingState);
    const [pagination, setPagination] = useState({
      pageIndex: pager?.page ?? 0,
      pageSize: pager?.rows ?? DEFAULT_ROWS,
    });
    const [columnFilters, setColumnFilters] = useState<Map<string, unknown>>(
      () => {
        // Initialize with initial filters if provided
        if (initialFilters && initialFilters.length > 0) {
          return transformInitialFiltersToMap(initialFilters, columns);
        }
        return new Map();
      },
    );
    /**
     * Keyed by `column.id`, NOT by the header's index within its header group.
     * With grouped headers there are multiple header groups, so indices collide
     * across them - one column's ref would overwrite another's, and
     * `handleRemoveFilter` would reset the wrong filter.
     */
    const filterRefs = useRef(new Map<string, DataTableFilterRef>());
    // Keep previous data while loading new page to avoid flickering
    const [displayData, setDisplayData] = useState(initialData);
    const [displayTotalElements, setDisplayTotalElements] =
      useState(totalElements);
    const [displayTotalPages, setDisplayTotalPages] = useState(totalPages);

    const { data, filterValues } = useMemo(() => {
      return prepareTableData(
        displayData,
        filterDataDisplayMap,
        textSearchKeys,
        skipFilterValuePreprocessing,
        columns,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayData, columns]);

    const [topOffset, setTopOffset] = useState(125);
    const outerContainerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      const el = outerContainerRef.current;
      if (!el) {
        return;
      }

      const updateTopOffset = () => {
        const rect = el.getBoundingClientRect();
        setTopOffset(rect.top);
      };

      updateTopOffset();

      const observer = new ResizeObserver(() => {
        updateTopOffset();
      });

      // Observe the parent to detect layout shifts (e.g. tabs, headers rendering)
      if (el.parentElement) {
        observer.observe(el.parentElement);
      }

      return () => {
        observer.disconnect();
      };
    }, []);

    // Update display data when new data arrives and not loading
    // This keeps old data visible while fetching the next page
    useEffect(() => {
      if (!isLoading) {
        setDisplayData(initialData);
      }
    }, [initialData, isLoading]);

    // Update display total elements when new value arrives and not loading
    // This keeps pagination visible and stable while fetching (server-side only)
    useEffect(() => {
      if (!clientControlled && !isLoading && totalElements !== undefined) {
        setDisplayTotalElements(totalElements);
      }
    }, [totalElements, isLoading, clientControlled]);

    // Update display total pages when new value arrives and not loading
    // This keeps pagination page count stable while fetching (server-side only)
    useEffect(() => {
      if (!clientControlled && !isLoading && totalPages !== undefined) {
        setDisplayTotalPages(totalPages);
      }
    }, [totalPages, isLoading, clientControlled]);

    /**
     * Every state slice EXCEPT `columnResizing`.
     *
     * `useTable`'s default selector subscribes to all registered slices with a
     * shallow compare, so `columnResizing.deltaOffset` - which updates on every
     * mousemove of a resize drag - re-rendered this entire component (~20
     * header cells, each with a Chakra Popover) on each mouse event. That is
     * the resize lag.
     *
     * `columnResizing` is consumed only by `ColumnResizeHandle`, which
     * subscribes to `table.atoms.columnResizing` itself, so excluding it here
     * confines per-mousemove rendering to the handle being dragged. Every other
     * slice still re-renders the table, which it must: widths come from
     * `columnSizing`, the row model from `columnFilters`/`sorting`/`pagination`,
     * and so on.
     */
    const selectStateWithoutColumnResizing = (
      tableState: TableState<AppFeatures>,
    ): DataTableSelectedState => ({
      columnFilters: tableState.columnFilters,
      columnOrder: tableState.columnOrder,
      columnPinning: tableState.columnPinning,
      columnSizing: tableState.columnSizing,
      columnVisibility: tableState.columnVisibility,
      expanded: tableState.expanded,
      pagination: tableState.pagination,
      rowSelection: tableState.rowSelection,
      sorting: tableState.sorting,
    });

    /**
     * Writes table-driven page changes out to `pager` here, at the source,
     * rather than from an effect on `pagination`. Such an effect would also
     * echo every inbound pager sync (see below) back out as a new write. When
     * `pager` is async - the atom-backed list pages - two rapid clicks leave
     * two writes in flight, each one's echo re-sends the other's page, and the
     * table ping-pongs between them until "Maximum update depth exceeded".
     */
    const handlePaginationChange = (updater: Updater<PaginationState>) => {
      const nextPagination = functionalUpdate(updater, pagination);
      // The table hands back the same object when nothing changed (e.g. a
      // click on the current page) - skip the write, as consumers key effects
      // on `pager` identity.
      if (nextPagination === pagination) {
        return;
      }
      setPagination(nextPagination);

      if (clientControlled || !pager || !setPager) {
        return;
      }

      setPager({
        ...pager,
        page: nextPagination.pageIndex,
        rows: nextPagination.pageSize,
        first: nextPagination.pageIndex * nextPagination.pageSize,
      });
    };

    const table = useTable<AppFeatures, TableData<any>, DataTableSelectedState>(
      {
        /**
         * Features and row models are registered once, at module scope, in
         * `appTable.ts`. The `manual*` flags below remain the only per-instance
         * switch: they short-circuit to the corresponding `pre*` row model even
         * when the factory is registered, so a server-controlled table never runs
         * client-side filtering/sorting/pagination.
         *
         * FIXME: `prepareTableData` still computes filter values by hand. Register
         * `columnFacetingFeature` + `facetedUniqueValues` in `appTable.ts` and
         * read `column.getFacetedUniqueValues()` instead.
         */
        features: appTableFeatures,
        defaultColumn: {
          minSize: 65,
        },
        columns,
        data,
        manualFiltering: !clientControlled,
        manualSorting: !clientControlled,
        manualPagination: !clientControlled || !enablePagination,
        columnResizeMode: 'onEnd',
        getRowId,
        getSubRows,
        onExpandedChange,
        // Destructured rather than spread: `customChildRow` also carries
        // `renderCustomExpandedRow`, which is not a table option.
        getRowCanExpand: customChildRow?.getRowCanExpand,

        // PAGINATION
        pageCount: displayTotalPages ?? -1, // -1 means unknown page count (client-side will calculate)

        // SORT
        enableMultiSort: false,
        enableSorting,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange: handlePaginationChange,
        /**
         * Global properties that can be access from anywhere using table.options.meta
         * Update declaration for any new properties: src/global.d.ts
         */
        meta: {
          selectionDisabled,
          filterValues,
          selectedFilters: columnFilters,
          onFilterUpdate: (columnId: string, value: unknown) => {
            setColumnFilters((prev) => {
              const column = table.getColumn(columnId);
              const filterVariant = column?.columnDef.meta?.filterVariant;
              if (isFilterValueEmpty(filterVariant, value)) {
                if (!prev.has(columnId)) {
                  return prev;
                }
                const newFilters = new Map(prev);
                newFilters.delete(columnId);
                return newFilters;
              }
              if (prev.get(columnId) === value) {
                return prev;
              }
              const newFilters = new Map(prev);
              newFilters.set(columnId, value);
              return newFilters;
            });
          },
        },
        /**
         * Anything provided here overrides the internal state of the table
         */
        state: {
          sorting,
          rowSelection,
          // v9 renamed column pinning to the logical `start`/`end` regions. The
          // public `pinnedLeftColumns`/`pinnedRightColumns` props keep their
          // physical names: this app is LTR-only, so start->left / end->right.
          columnPinning: {
            start: pinnedLeftColumns ?? EMPTY_PINNED_COLUMNS,
            end: pinnedRightColumns ?? EMPTY_PINNED_COLUMNS,
          },
          pagination,
          ...state,
        },
      },
      selectStateWithoutColumnResizing,
    );

    const { rows } = table.getRowModel();

    /**
     * Stable identity so a consumer passing an inline `onRowClick` arrow does
     * not invalidate every memoised row on each render.
     */
    const handleRowClick = useCallbackRef(
      (row: any, tableRow: AppRow<any>, openInNewWindow: boolean) => {
        onRowClick?.(row, tableRow, openInNewWindow);
      },
    );

    const { columnSizing } = table.store.state;

    const rowRenderContext = useMemo<RowRenderContext>(
      () => ({
        tableProps,
        selectionDisabled,
        columnSizing,
        onRowClick: handleRowClick,
      }),
      [tableProps, selectionDisabled, columnSizing, handleRowClick],
    );

    useImperativeHandle(ref, (): DataTableRef => ({
      resetFilters: () => {
        filterRefs.current.forEach((ref) => ref.resetFilters());
      },
      setFilterValue: (options) => {
        filterRefs.current.forEach((ref) => ref.setFilterValue(options));
      },
      getSelectedRows: () =>
        table.getSelectedRowModel().rows.map(({ original }) => original),
      getVisibleRows: () =>
        table.getRowModel().rows.map(({ original }) => original),
      resetRowSelection: () => table.resetRowSelection(),
    }));

    const handleRemoveFilter = (columnId: string) => {
      const column = table.getColumn(columnId);
      const filterKey = column ? getColumnFilterKey(column) : columnId;

      setColumnFilters((prev) => {
        if (!prev.has(columnId) && !prev.has(filterKey)) {
          return prev;
        }
        const newFilters = new Map(prev);
        newFilters.delete(columnId);
        newFilters.delete(filterKey);
        return newFilters;
      });

      filterRefs.current.get(columnId)?.resetFilters(true);
    };

    /**
     * Equivalent to `getSelectedRowModel().rows.length`: that model is built
     * from the core row model, so both count selected rows that are currently
     * filtered out, and stale ids are pruned by the effect below.
     */
    useNonInitialEffect(() => {
      onSelectedRowCountChange?.(Object.keys(rowSelection).length);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowSelection]);

    // Handle filter changes
    useNonInitialEffect(() => {
      if (clientControlled) {
        return;
      }

      const transformedFilters = transformFiltersForServer(
        columnFilters,
        (columnId) => table.getColumn(columnId),
      );

      // Call filter change callback if provided
      if (onFiltersChange) {
        onFiltersChange(transformedFilters);
      }

      // Reset pagination to page 0 when filters change. This also resets the
      // external pager, via `handlePaginationChange`.
      const currentPageIndex = table.store.state.pagination.pageIndex;
      if (currentPageIndex !== 0) {
        table.setPageIndex(0);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnFilters]);

    // Handle sorting changes
    useNonInitialEffect(() => {
      if (clientControlled || !pager || !setPager) {
        return;
      }

      setPager({
        ...pager,
        sortField: sorting.length > 0 ? sorting[0].id : undefined,
        sortOrder: sorting.length > 0 ? (sorting[0].desc ? -1 : 1) : undefined,
      });

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting]);

    /**
     * Sync an externally-driven `pager` into the table's pagination state
     * (server-controlled mode only), so the table follows a parent that changes
     * the page itself.
     *
     * Keyed on `pager.page`/`pager.rows` rather than on `pager`, and it writes
     * through `setPagination` rather than `table.setPageIndex`. Both matter:
     *
     * - v9's `useTable` returns a NEW table object on every render - its
     *   `useMemo` is keyed on the inline options literal, which never compares
     *   equal - so listing `table` as a dep made this run on every render
     *   rather than only when `pager` changed. `pager` lags `pagination` by at
     *   least one render (`handlePaginationChange` writes it), so the guard was
     *   always true and each run wrote the *stale* page back. The table then
     *   oscillated between the clicked page and the previous one until React
     *   bailed out with "Maximum update depth exceeded".
     * - Calling `setPagination` directly also bypasses `handlePaginationChange`,
     *   so following the pager never writes back to it.
     * - `pagination` is a controlled state slice, so it - not the table store -
     *   is the source of truth. Returning `prev` unchanged lets React skip the
     *   re-render, which is what actually terminates the cycle.
     * - `table.setPageIndex` also clamps against `options.pageCount`, which is
     *   deliberately held stale while a fetch is in flight, so jumping to a
     *   late page could land on an earlier one.
     */
    const hasExternalPager = !clientControlled && !!pager;
    const externalPageIndex = pager?.page ?? 0;
    const externalPageSize = pager?.rows ?? DEFAULT_ROWS;

    useEffect(() => {
      if (!hasExternalPager) {
        return;
      }

      setPagination((prev) =>
        prev.pageIndex === externalPageIndex &&
        prev.pageSize === externalPageSize
          ? prev
          : { pageIndex: externalPageIndex, pageSize: externalPageSize },
      );
    }, [hasExternalPager, externalPageIndex, externalPageSize]);

    useEffect(() => {
      onVisibleRowCountChange?.(rows.length);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    /**
     * If data changes and selected rows are no longer included in dataset
     * then we need to ensure they are removed from the selection or else the
     * table state does not work as expected since items are selected that do not exist.
     */
    useNonInitialEffect(() => {
      setRowSelection((prev) => {
        // Building the id set is O(rows), so only pay for it when something is
        // actually selected.
        if (Object.keys(prev).length === 0) {
          return prev;
        }
        const allRowIds = new Set(data.map(getRowId));
        return pruneRowSelection(prev, (rowId) => allRowIds.has(rowId));
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Determine if filters are applied
    const filtersApplied = columnFilters.size > 0;
    const totalRecords = displayTotalElements ?? totalElements ?? data.length;

    const getDefaultColumnFilters = () => {
      const defaultFilters = new Map<string, unknown>();
      table.getAllLeafColumns().forEach((column) => {
        const defaultValue = column.columnDef.meta?.booleanFilter?.defaultValue;
        if (defaultValue && defaultValue !== 'ALL') {
          defaultFilters.set(column.id, defaultValue);
        }
      });
      return defaultFilters;
    };

    const handleResetAllFilters = () => {
      filterRefs.current.forEach((filterRef) => filterRef.resetFilters());
      setColumnFilters((prev) => {
        const defaultFilters = getDefaultColumnFilters();
        const isUnchanged =
          prev.size === defaultFilters.size &&
          Array.from(defaultFilters).every(
            ([columnId, value]) => prev.get(columnId) === value,
          );
        return isUnchanged ? prev : defaultFilters;
      });
      resetFilter?.();
    };

    // Determine which empty/loading state to render (outside of the table)
    const renderEmptyOrLoadingState = () => {
      // Only show skeleton on initial load (no existing data to display).
      // On subsequent fetches (filter/sort/page changes), the progress bar
      // handles the loading indication while stale data remains visible.
      if (isLoading && data.length === 0) {
        return (
          <MBox>
            <MSkeleton height="50px" />
            {Array.from({ length: 15 }).map((_, i) => (
              <MSkeleton key={i} height="50px" mt="1" />
            ))}
          </MBox>
        );
      }

      if (totalRecords > 0 || isLoading) {
        return null;
      }

      // singleEmptyPlaceholder !== true : use separate logic for case where filters not applied
      if (!singleEmptyPlaceholder && !filtersApplied) {
        return emptyProps?.renderEmptyPlaceholder ? (
          emptyProps.renderEmptyPlaceholder()
        ) : (
          <MEmptyDataPlaceholder {...emptyProps} />
        );
      }

      // singleEmptyPlaceholder === true : use the same logic whether or not empty results are due to applied filters or not
      if (singleEmptyPlaceholder || filtersApplied) {
        const {
          mainMessage,
          smallMessage,
          btnLabel,
          to,
          onReload,
          secondaryBtnLabel,
        } = emptyFilterResultsProps || {};
        return (
          <MEmptyDataPlaceholder
            mainMessage={
              mainMessage ||
              'The filter you applied did not return any results.'
            }
            smallMessage={
              smallMessage === null
                ? ''
                : (smallMessage ??
                  'Try resetting or updating the filter if you expected to see results')
            }
            btnLabel={btnLabel}
            onClick={to ? undefined : onReload || handleResetAllFilters}
            to={to ? to : undefined}
            secondaryBtnLabel={secondaryBtnLabel}
            onClickSecondaryBtn={handleResetAllFilters}
          />
        );
      }

      return null;
    };

    const emptyOrLoadingContent = renderEmptyOrLoadingState();

    return (
      <MFlex
        ref={outerContainerRef}
        flexDirection="column"
        width="100%"
        maxHeight={`calc(100vh - ${topOffset}px)`}
        pb={enablePagination ? undefined : 8}
        {...containerProps}
      >
        <DataTableActiveFilters
          table={table}
          columnFilters={columnFilters}
          onRemoveFilter={handleRemoveFilter}
          onResetAll={handleResetAllFilters}
          extraFilters={extraActiveFilters}
        />
        <Progress.Root
          size="xs"
          value={null}
          style={{
            visibility: isLoading ? 'visible' : 'hidden',
          }}
          variant="neutral"
        >
          <Progress.Track height="2px">
            <Progress.Range />
          </Progress.Track>
        </Progress.Root>
        {emptyOrLoadingContent || (
          <Table.ScrollArea
            ref={tableContainerRef}
            w="100%"
            flex="1 1 auto"
            minHeight="0"
            overflowY="auto"
            {...extraProps.containerProps}
          >
            <Table.Root
              size="sm"
              display="grid"
              {...tableProps.table}
              {...extraProps.tableProps}
            >
              <Table.Header
                position="sticky"
                top={0}
                zIndex={4}
                bg="white"
                {...extraProps.tableHeadProps}
              >
                {table.getHeaderGroups().map((headerGroup) => {
                  const lastNonPinnedHeaderIndex = findLastNonPinnedIndex(
                    headerGroup.headers,
                  );
                  return (
                    <Table.Row
                      key={headerGroup.id}
                      borderBottom="1px solid"
                      borderBottomColor={borderColor}
                      {...tableProps.headRow}
                      _hover={{
                        backgroundColor: 'inherit',
                      }}
                      display="flex"
                      width="100%"
                      role="group"
                    >
                      {headerGroup.headers.map((header, i) => {
                        const isPinned = header.column.getIsPinned();
                        const sortable = header.column.getCanSort();
                        const sorted = header.column.getIsSorted();
                        const canResize = header.column.getCanResize();
                        const isGroup = header.subHeaders.length > 0;
                        const hasColSpan = header.colSpan && header.colSpan > 1;
                        const hasBorder = !isGroup || !header.isPlaceholder;
                        const isFirstColumn = i === 0;
                        // Check if this is the last non-pinned column (should grow to fill space)
                        const isLastNonPinned = i === lastNonPinnedHeaderIndex;
                        // Calculate the offset for pinned columns
                        const pinnedOffset = isPinned
                          ? `${header.column.getStart(isPinned)}px`
                          : undefined;

                        const headerBorderStyles = getHeaderBorderStyles({
                          isPinned,
                          isFirstColumn,
                          isGroup,
                          skipBorder: isGroup && !!isPinned,
                        });

                        return (
                          <Table.ColumnHeader
                            key={header.id}
                            colSpan={header.colSpan}
                            display="flex"
                            /*
                             * Explicit `px`: a bare number is resolved against
                             * Chakra's `sizes` scale, and sizes that collide
                             * with a token key (72 -> 18rem) render four times
                             * too wide.
                             */
                            width={`${header.getSize()}px`}
                            flex={isLastNonPinned ? '1 0 auto' : '0 0 auto'}
                            {...headerBorderStyles}
                            backgroundColor={isPinned ? 'white' : undefined}
                            zIndex={isPinned ? 1 : undefined}
                            position={isPinned ? 'sticky' : undefined}
                            left={
                              isPinned === 'start' ? pinnedOffset : undefined
                            }
                            right={
                              isPinned === 'end' ? pinnedOffset : undefined
                            }
                            pt={tableProps?.headCell?.pt ?? '0 !important'}
                            pb={tableProps?.headCell?.pb ?? '0 !important'}
                            pr={tableProps?.headCell?.pr ?? '0 !important'}
                            pl={tableProps?.headCell?.pl ?? '0 !important'}
                            {...tableProps.headCell}
                          >
                            {header.id === ACTION_COLUMN_KEY ? null : (
                              <MBox position="relative" w="100%" h="100%">
                                {header.isPlaceholder ? null : (
                                  <MFlex p="0.4rem" alignItems="center">
                                    {/*
                                     * The caret only takes space while the
                                     * column is sorted: an empty slot kept for
                                     * it pushed the filter icon 20px further
                                     * from sortable labels than unsortable
                                     * ones. Centered, not nudged with a
                                     * negative margin, so it lines up with the
                                     * label and the filter icon.
                                     */}
                                    <MFlex
                                      alignItems="center"
                                      cursor={sortable ? 'pointer' : 'default'}
                                      justifyContent={
                                        hasColSpan ? 'center' : undefined
                                      }
                                      _hover={{
                                        textDecoration: sortable
                                          ? 'underline'
                                          : 'none',
                                      }}
                                      onClick={header.column.getToggleSortingHandler()}
                                    >
                                      <MBox as="span">
                                        {flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        )}
                                      </MBox>
                                      {sortable && sorted && (
                                        <Icon
                                          ml={1}
                                          color="tPurple.dark"
                                          asChild
                                        >
                                          {sorted === 'asc' ? (
                                            <FaCaretUp />
                                          ) : (
                                            <FaCaretDown />
                                          )}
                                        </Icon>
                                      )}
                                    </MFlex>
                                    {header.column.getCanFilter() && (
                                      <DateTableHeaderFilter
                                        ref={(headerRef) => {
                                          if (headerRef) {
                                            filterRefs.current.set(
                                              header.column.id,
                                              headerRef,
                                            );
                                          } else {
                                            filterRefs.current.delete(
                                              header.column.id,
                                            );
                                          }
                                        }}
                                        column={header.column}
                                        table={table}
                                        clientControlled={clientControlled}
                                      />
                                    )}
                                  </MFlex>
                                )}
                                {hasBorder && canResize && (
                                  <ColumnResizeHandle
                                    table={table}
                                    header={header}
                                  />
                                )}
                              </MBox>
                            )}
                          </Table.ColumnHeader>
                        );
                      })}
                    </Table.Row>
                  );
                })}
              </Table.Header>
              {enableVirtualization ? (
                <VirtualizedTableBody
                  rows={rows}
                  tableContainerRef={tableContainerRef}
                  rowRenderContext={rowRenderContext}
                />
              ) : (
                <StandardTableBody
                  rows={rows}
                  tableProps={tableProps}
                  onRowClick={onRowClick}
                  customChildRow={customChildRow}
                />
              )}
            </Table.Root>
          </Table.ScrollArea>
        )}
        {enablePagination && !loadMoreProps && (
          <DataTablePagination
            table={table}
            totalElements={displayTotalElements}
            isLoading={isLoading}
          />
        )}
        {loadMoreProps && !enablePagination && (
          <LoadMore
            fetchedElementLength={data.length}
            totalElements={totalElements}
            isLoading={isLoading}
            hasMoreItem={loadMoreProps.hasMoreItem}
            onLoadMore={loadMoreProps.onLoadMore}
          />
        )}
      </MFlex>
    );
  },
);

DataTableComponent.displayName = 'DataTable';

/**
 * Memoised so that consumer state changes unrelated to the table (most notably
 * a selected-row count) do not re-render the whole table.
 *
 * Safe because no consumer mutates `columns` or `data` in place — they come
 * from `useState`/React Query respectively. Consumers passing inline callbacks
 * or object literals simply never bail out, so the risky cases self-exclude.
 */
export const DataTable = memo(DataTableComponent);

/**
 * Drag handle for column resizing.
 *
 * Subscribes to the `columnResizing` state slice alone, via v9's per-slice
 * atoms, instead of reading it during `DataTable`'s render. `deltaOffset`
 * updates on every mousemove of a drag, so reading it higher up re-rendered the
 * entire header row (and every filter popover in it) on each mouse event. Now
 * only the handle being dragged re-renders.
 */
function ColumnResizeHandle({
  table,
  header,
}: {
  table: AppReactTable<TableData<any>, unknown>;
  header: AppHeader<TableData<any>, unknown>;
}) {
  return (
    <table.Subscribe
      source={table.atoms.columnResizing}
      selector={(columnResizing) => columnResizing.deltaOffset ?? 0}
    >
      {(deltaOffset) => {
        const isResizing = header.column.getIsResizing();
        return (
          <MBox
            // Per-column so the id is unique on screen; the resize affordance
            // is otherwise an unlabeled box with no way for QA to target it.
            data-testid={`column-resize-handle-${header.column.id}`}
            position="absolute"
            top="0"
            right="0"
            height="100%"
            width="4px"
            bg="tIndigo.base"
            opacity={isResizing ? 1 : 0}
            cursor="col-resize"
            userSelect="none"
            _hover={{
              opacity: 1,
            }}
            transform={isResizing ? `translateX(${deltaOffset}px)` : undefined}
            onDoubleClick={() => header.column.resetSize()}
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
          />
        );
      }}
    </table.Subscribe>
  );
}

function renderRowCells<T extends object>(
  visibleCells: AppCell<T, unknown>[],
  isRowSelected: boolean,
  tableProps: ExtraTableProps,
) {
  const lastNonPinnedCellIndex = findLastNonPinnedIndex(visibleCells);

  return visibleCells.map((cell, cellIndex) => {
    const isPinned = cell.column.getIsPinned();
    const cellStyles = cell.column.columnDef.meta?.cellStyles ?? {};
    const pinnedOffset = isPinned
      ? `${cell.column.getStart(isPinned)}px`
      : undefined;
    const cellBorderStyles = getCellBorderStyles(isPinned);
    const isLastNonPinnedCell = cellIndex === lastNonPinnedCellIndex;

    return (
      <Table.Cell
        key={cell.id}
        position={isPinned ? 'sticky' : undefined}
        left={isPinned === 'start' ? pinnedOffset : undefined}
        right={isPinned === 'end' ? pinnedOffset : undefined}
        {...cellBorderStyles}
        display="flex"
        alignItems="center"
        // Explicit `px` - see the matching note on the header cell.
        width={`${cell.column.getSize()}px`}
        flex={isLastNonPinnedCell ? '1 0 auto' : '0 0 auto'}
        whiteSpace="normal"
        borderTop="none"
        zIndex={isPinned ? 1 : undefined}
        backgroundColor={isRowSelected ? '#fafaff' : 'white'}
        paddingInlineStart={2}
        css={CELL_HOVER_STYLES}
        {...tableProps.cell}
        {...cellStyles}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </Table.Cell>
    );
  });
}

interface TableBodySharedProps<T extends object> {
  rows: AppRow<T>[];
  tableProps: ExtraTableProps;
  onRowClick?: (row: T, tableRow: AppRow<T>, openInNewWindow: boolean) => void;
}

/**
 * Invalidation boundary for `VirtualizedTableRow`.
 *
 * `columnSizing` and `selectionDisabled` are not read by the row component
 * itself — they are here because *cells* read them off the table instance
 * (`column.getSize()` / `column.getStart()` and `meta.selectionDisabled`), and
 * those reads are invisible to a shallow prop comparison. Without them, e.g.
 * resizing a column while rows are selected would leave stale widths.
 */
interface RowRenderContext {
  tableProps: ExtraTableProps;
  selectionDisabled?: boolean;
  columnSizing: ColumnSizingState;
  onRowClick: (
    row: any,
    tableRow: AppRow<any>,
    openInNewWindow: boolean,
  ) => void;
}

interface VirtualizedTableRowProps {
  row: AppRow<any>;
  /**
   * Passed explicitly rather than derived inside the row so that a change to
   * the visible column set / order / pinning invalidates the memo.
   */
  visibleCells: AppCell<any, unknown>[];
  isRowSelected: boolean;
  virtualIndex: number;
  virtualStart: number;
  measureElement: (node: HTMLTableRowElement | null) => void;
  context: RowRenderContext;
}

const VirtualizedTableRow = memo(function VirtualizedTableRow({
  row,
  visibleCells,
  isRowSelected,
  virtualIndex,
  virtualStart,
  measureElement,
  context,
}: VirtualizedTableRowProps) {
  const { onRowClick, tableProps } = context;

  return (
    <Table.Row
      data-row
      data-index={virtualIndex}
      ref={measureElement}
      borderBottom="1px solid #e4e4e4"
      _last={ROW_LAST_STYLES}
      _hover={ROW_HOVER_STYLES}
      aria-selected={isRowSelected}
      {...tableProps.bodyRow}
      display="flex"
      position="absolute"
      transform={`translateY(${virtualStart}px)`}
      width="100%"
      css={ROW_EXPANDED_ZINDEX_STYLES}
      onClick={(e) => {
        if (!isEventFromRowDom(e)) {
          return;
        }
        const openInNewWindow = e.metaKey || e.ctrlKey || e.shiftKey;
        onRowClick(row.original, row, openInNewWindow);
      }}
      onPointerDown={(e) => {
        // mouse center button
        if (e.button === 1 && isEventFromRowDom(e)) {
          e.preventDefault();
          onRowClick(row.original, row, true);
        }
      }}
      cursor="pointer"
    >
      {renderRowCells(visibleCells, isRowSelected, tableProps)}
    </Table.Row>
  );
});

function VirtualizedTableBody<T extends object>({
  rows,
  tableContainerRef,
  rowRenderContext,
}: Pick<TableBodySharedProps<T>, 'rows'> & {
  tableContainerRef: React.RefObject<HTMLDivElement>;
  rowRenderContext: RowRenderContext;
}) {
  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => 62,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 15,
  });

  return (
    <Table.Body
      display="grid"
      height={`${rowVirtualizer.getTotalSize() + 5}px`}
      position="relative"
      {...rowRenderContext.tableProps.body}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <VirtualizedTableRow
            key={row.id}
            row={row as AppRow<any>}
            visibleCells={row.getVisibleCells()}
            isRowSelected={row.getIsSelected()}
            virtualIndex={virtualRow.index}
            virtualStart={virtualRow.start}
            // Stable instance method — an inline callback would make React
            // detach/reattach every visible row's ref on every render, forcing
            // a synchronous layout per row.
            measureElement={rowVirtualizer.measureElement}
            context={rowRenderContext}
          />
        );
      })}
    </Table.Body>
  );
}

function StandardTableBody<T extends object>({
  rows,
  tableProps,
  onRowClick,
  customChildRow,
}: TableBodySharedProps<T> & {
  customChildRow?: DataTableProps<T>['customChildRow'];
}) {
  return (
    <Table.Body {...tableProps.body}>
      {rows.map((row, index) => {
        const isRowSelected = row.getIsSelected();
        return (
          <Fragment key={row.id}>
            <Table.Row
              data-row
              data-index={index}
              borderBottom="1px solid #e4e4e4"
              _last={ROW_LAST_STYLES}
              _hover={{
                backgroundColor: '#fafaff',
                borderBottomColor: '#4659da',
              }}
              aria-selected={isRowSelected}
              {...tableProps.bodyRow}
              display="flex"
              width="100%"
              css={{
                '&:has([aria-expanded="true"])': {
                  zIndex: 10,
                },
              }}
              onClick={(e) => {
                if (!isEventFromRowDom(e)) {
                  return;
                }
                const openInNewWindow = e.metaKey || e.ctrlKey || e.shiftKey;
                onRowClick?.(row.original, row, openInNewWindow);
              }}
              onPointerDown={(e) => {
                // mouse center button
                if (e.button === 1 && isEventFromRowDom(e)) {
                  e.preventDefault();
                  onRowClick?.(row.original, row, true);
                }
              }}
              cursor="pointer"
            >
              {renderRowCells(row.getVisibleCells(), isRowSelected, tableProps)}
            </Table.Row>
            {customChildRow && row.getIsExpanded() && (
              <Table.Row
                borderBottom="1px solid #e4e4e4"
                _last={ROW_LAST_STYLES}
                display="flex"
                width="100%"
                _hover={{
                  backgroundColor: 'inherit',
                }}
              >
                <Table.Cell
                  colSpan={row.getVisibleCells().length}
                  p={0}
                  display="block"
                  flex={1}
                >
                  {customChildRow.renderCustomExpandedRow(row)}
                </Table.Cell>
              </Table.Row>
            )}
          </Fragment>
        );
      })}
    </Table.Body>
  );
}
